import React from "react";
import styled from "styled-components";
import { CompassParagraph } from "../common/styledComponents";
import {
  ColumnContainer,
  ElementContainer,
  FlexibleContainer,
} from "../containers/styledComponents";
import {
  compassLightGreyBackground,
  compassLightGreen,
  compassYellow,
  brightRed,
  compassYellowBackground,
  compassLightRed,
  lightGreenBackground,
  greyText,
} from "../../styles";
import { MESSAGES } from "../../messages";
import { IconContainer } from "./styledComponents";
import { LoadingTableCell } from "../tables/LoadingTableCell";
import { PortfoliosTempData } from "../../models/controlPanelDataModel";
import { Metric } from "../../models/tableModels";

const StyledProgress = styled.progress<{ color: string }>`
  padding-right: 15px;
  height: 3px;
  border: none;

  &::-webkit-progress-bar {
    background-color: ${compassLightGreyBackground};
    border-radius: 2px;
  }

  &::-webkit-progress-value {
    background-color: ${({ color }): string => color};
    border-radius: 2px;
  }

  &::-moz-progress-bar {
    background-color: ${({ color }): string => color};
    border-radius: 2px;
  }
`;

export enum AlignmentTemperature {
  ONE_SEVEN_FIVE = "<1.75C°",
  TWO = "<2C°",
  THREE = "<3C°",
  ABOVE_THREE = ">3C°",
}

export const PATHWAYS: Pathway[] = [
  {
    value_text: MESSAGES.NOT_AVAILABLE_TEXT,
    value_numeric: 0,
    color: greyText,
    bgColor: compassLightGreyBackground,
    diagnostic: "Unavailable",
  },
  {
    value_text: "<1.75°C",
    value_numeric: 1,
    color: compassLightGreen,
    bgColor: lightGreenBackground,
    diagnostic: "Aligned",
  },
  {
    value_text: "<2°C",
    value_numeric: 2,
    color: compassLightGreen,
    bgColor: lightGreenBackground,
    diagnostic: "Aligned",
  },
  {
    value_text: "2-3°C",
    value_numeric: 3,
    color: compassYellow,
    bgColor: compassYellowBackground,
    diagnostic: "Misaligned",
  },
  {
    value_text: ">3°C",
    value_numeric: 4,
    color: brightRed,
    bgColor: compassLightRed,
    diagnostic: " Very Misaligned",
  },
];

export const getPathwayFromAlignment = (
  alignment: AlignmentTemperature
): Pathway => {
  switch (alignment) {
    case AlignmentTemperature.ONE_SEVEN_FIVE:
      return PATHWAYS[1];
    case AlignmentTemperature.TWO:
      return PATHWAYS[2];
    case AlignmentTemperature.THREE:
      return PATHWAYS[3];
    case AlignmentTemperature.ABOVE_THREE:
      return PATHWAYS[4];
  }
};

export const getPathwayFromInput = (input: string): Pathway => {
  const HIGHER_THRESHOLD = 3;
  const LOWER_THRESHOLD = 2;
  const MINIMAL_THRESHOLD = 1.75;

  // Extract the last temperature and operator
  const temperatures = input.match(/[-+]?\d*\.?\d+/g);
  const operators = input.match(/[<>]/g);

  // Default to pathway 0 if no temperature is found
  let pathwayIndex: 0 | 1 | 2 | 3 | 4 = 3;

  if (temperatures) {
    const temperature = Math.abs(
      parseFloat(temperatures[temperatures.length - 1])
    );

    // Determine the default pathway based on the temperature
    if (temperature <= MINIMAL_THRESHOLD) {
      pathwayIndex = 1;
    }
    if (temperature <= LOWER_THRESHOLD) {
      pathwayIndex = 2;
    }
    if (temperature > HIGHER_THRESHOLD) {
      pathwayIndex = 4;
    }

    // Modify the pathway if there's an operator
    if (operators) {
      const operator = operators[operators.length - 1];

      if (operator === "<" && temperature <= LOWER_THRESHOLD) {
        pathwayIndex = 1;
      } else if (operator === ">" && temperature >= HIGHER_THRESHOLD) {
        pathwayIndex = 4;
      }
    }
  }

  // Return the correct pathway based on pathwayIndex
  return PATHWAYS[pathwayIndex];
};

export const getRandomPathway = (): Pathway => {
  const pathwaysWithoutUnavailable = PATHWAYS.slice(1, 4);
  const randomIndex = Math.floor(
    Math.random() * pathwaysWithoutUnavailable.length
  );

  return pathwaysWithoutUnavailable[randomIndex];
};

export const checkAlignment = (
  data: PortfoliosTempData | Metric
): AlignmentTemperature => {
  let pathway = AlignmentTemperature.ABOVE_THREE;

  if (data.tCO2e_2_7C_or_3C > 0) {
    pathway = AlignmentTemperature.ABOVE_THREE;
  }
  if (data.tCO2e_2_7C_or_3C < 0) {
    pathway = AlignmentTemperature.THREE;
  }
  if (data.tCO2e_2C < 0) {
    pathway = AlignmentTemperature.TWO;
  }
  if (data.tCO2e_1_5C_or_1_75C < 0) {
    pathway = AlignmentTemperature.ONE_SEVEN_FIVE;
  }

  return pathway;
};

export const PathwayProgressBar = ({
  pathwayItem,
}: PathwayProps): JSX.Element => {
  const { value_text, value_numeric, color } = pathwayItem;

  return (
    <ColumnContainer ai="flex-start">
      <CompassParagraph color={color} fontSize={12}>
        {value_text}
      </CompassParagraph>
      <StyledProgress value={value_numeric} max={4} color={color} />
    </ColumnContainer>
  );
};

export const PathwayBox: React.FC<PathwayProps<Pathway>> = ({
  pathwayItem,
  isLoading = false,
}) => {
  return (
    <FlexibleContainer ml="18px" mr="12px">
      <IconContainer
        bg={pathwayItem.bgColor}
        br={15}
        size={40}
        isLoading={isLoading}
      >
        <CompassParagraph
          ml={8}
          fontSize={12}
          bold={true}
          color={pathwayItem.color}
        >
          {!isLoading && pathwayItem.value_text}
        </CompassParagraph>
      </IconContainer>
      {isLoading ? (
        <LoadingTableCell dark={true} ml={1} />
      ) : (
        <ElementContainer flexDirection="column">
          <CompassParagraph fontSize={15}>
            {pathwayItem.diagnostic}
          </CompassParagraph>
          <CompassParagraph color={greyText} fontSize={11}>
            {
              MESSAGES.ALIGNMENT_COMMITMENT[
                pathwayItem.value_text === MESSAGES.NOT_AVAILABLE_TEXT
                  ? "NOT_AVAILABLE"
                  : "COMMITTED"
              ]
            }
          </CompassParagraph>
        </ElementContainer>
      )}
    </FlexibleContainer>
  );
};

export type PathwayItem = {
  value_text: string;
  value_numeric: number;
  color: string;
};

export type PathwayProps<T extends Pathway | PathwayItem = PathwayItem> = {
  pathwayItem: T;
  isLoading?: boolean;
};

export interface Pathway extends PathwayItem {
  bgColor: string;
  diagnostic: string;
}
